var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { Flex, Box } from 'rebass';
import classnames from 'classnames';
import Text from '../Text';
import variants from './Input.variants';
var ifEnter = function (callback) { return function (event) {
    if (callback && event.key === 'Enter') {
        event.preventDefault();
        callback(event.currentTarget.value);
    }
}; };
var inputSx = __assign({ border: '1px solid', borderRadius: 'normal', boxSizing: 'border-box', outline: 0, 'input&': {
        minHeight: '40px',
    }, '& input': {
        minHeight: '38px',
    }, px: 3, resize: 'none', transition: 'all ease .2s', '&.block': {
        borderRadius: 0,
    }, '&.darkenPlaceholder::placeholder, &.darkenPlaceholder input::placeholder': {
        color: 'inherit !important',
        opacity: '1 !important',
    }, '&.noBorder': {
        bg: 'transparent',
        border: 'none',
    }, '&.widthAuto': {
        minWidth: '208px',
    }, '&.fullHeight, & .fullHeight': {
        height: '100%',
    }, 'textarea&': {
        overflowY: 'auto',
        py: 2,
        '&:not(.fullHeight)': {
            maxHeight: '60px',
        },
    } }, Object.fromEntries(Object.entries(variants).map(function (_a) {
    var k = _a[0], v = _a[1];
    return ["&.variant--" + k, v];
})));
var Input = React.forwardRef(function (_a, ref) {
    var _b;
    var active = _a.active, affixBottom = _a.affixBottom, block = _a.block, containerProps = _a.containerProps, customPrefix = _a.customPrefix, darkenPlaceholder = _a.darkenPlaceholder, fullHeight = _a.fullHeight, fullWidth = _a.fullWidth, invalid = _a.invalid, noBorder = _a.noBorder, onChange = _a.onChange, onChangeValue = _a.onChangeValue, onKeyDown = _a.onKeyDown, onReturn = _a.onReturn, placeholder = _a.placeholder, prefix = _a.prefix, rows = _a.rows, suffix = _a.suffix, value = _a.value, _c = _a.variant, variant = _c === void 0 ? 'default' : _c, width = _a.width, props = __rest(_a, ["active", "affixBottom", "block", "containerProps", "customPrefix", "darkenPlaceholder", "fullHeight", "fullWidth", "invalid", "noBorder", "onChange", "onChangeValue", "onKeyDown", "onReturn", "placeholder", "prefix", "rows", "suffix", "value", "variant", "width"]);
    var keyDown = function (e) {
        onKeyDown && onKeyDown(e);
        onReturn && ifEnter(onReturn)(e);
    };
    var change = function (e) {
        onChange && onChange(e);
        onChangeValue && onChangeValue(e.currentTarget.value);
    };
    var asProps = __assign({ as: props.textarea ? 'textarea' : 'input' }, (props.textarea ? { rows: rows === undefined ? 1 : rows } : {}));
    var widthWithFull = fullWidth ? '100%' : width;
    var className = classnames((_b = {
            active: active,
            block: block,
            invalid: invalid,
            disabled: props.disabled,
            widthAuto: !widthWithFull,
            fullHeight: fullHeight,
            noBorder: noBorder,
            darkenPlaceholder: darkenPlaceholder
        },
        _b["variant--" + variant] = !!variant,
        _b));
    var p = props.p, px = props.px, py = props.py, pt = props.pt, pr = props.pr, pb = props.pb, pl = props.pl, m = props.m, mx = props.mx, my = props.my, mt = props.mt, mr = props.mr, mb = props.mb, ml = props.ml, otherProps = __rest(props, ["p", "px", "py", "pt", "pr", "pb", "pl", "m", "mx", "my", "mt", "mr", "mb", "ml"]);
    var outerProps = __assign({ m: m,
        mx: mx,
        my: my,
        mt: mt,
        mr: mr,
        mb: mb,
        ml: ml,
        p: p,
        px: px,
        py: py,
        pt: pt,
        pr: pr,
        pb: pb,
        pl: pl,
        className: className, width: widthWithFull, sx: inputSx }, containerProps);
    var baseInputProps = {
        py: 2,
    };
    var inputProps = __assign(__assign(__assign(__assign(__assign({}, baseInputProps), otherProps), { ref: ref,
        value: value }), asProps), { onChange: change, onKeyDown: keyDown });
    if (!prefix && !suffix && !customPrefix && !affixBottom)
        return (React.createElement(Text, __assign({}, outerProps, inputProps, { placeholder: placeholder, variant: "bodySecondary" })));
    var complexPlaceholder = typeof placeholder !== 'string';
    return (React.createElement(Text, __assign({ as: "div", display: "inline-block" }, outerProps, { variant: "bodySecondary" }, (customPrefix && { pl: 0 })),
        React.createElement(Flex, { alignItems: "center", sx: { height: '100%' } },
            React.createElement(Flex, { flex: "1 1", flexWrap: "wrap", alignItems: "center", sx: __assign({}, (fullHeight && { height: '100%' })) },
                prefix && (React.createElement(Flex, { alignSelf: "stretch", alignItems: "center", pr: 1, mr: 2, sx: { borderRight: '1px solid', borderColor: 'neutral_5' } }, prefix)),
                customPrefix && customPrefix,
                React.createElement(Box, { flex: "1 1", sx: __assign({ position: 'relative' }, (fullHeight && { height: '100%' })) },
                    complexPlaceholder && value == '' && (React.createElement(Text, __assign({}, baseInputProps, { as: "div", children: placeholder, sx: {
                            position: 'absolute',
                            height: '100%',
                            width: '100%',
                            zIndex: 1,
                            whiteSpace: 'nowrap',
                        } }))),
                    React.createElement(Flex, { flexDirection: "column", sx: { height: '100%' } },
                        React.createElement(Text, __assign({ placeholder: complexPlaceholder ? undefined : placeholder, flex: "1 1", sx: __assign({ position: 'relative', zIndex: 2, border: '0 none', bg: 'transparent', color: 'inherit', fontSize: 'inherit', outline: 0, width: '100%', resize: 'none' }, (fullHeight && { height: '100%' })) }, inputProps)),
                        affixBottom && affixBottom))),
            suffix && React.createElement(Flex, { flex: "0 0 auto" }, suffix))));
});
export default Input;
